import { useEffect } from 'react';

import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { useRouter } from '@/hooks/useRouter';
import { isWholeSalerWidget } from '@/lib/const';
import { EVENT_NAME } from '@/lib/gtm/const';
import type { WithChildren } from '@/lib/types';
import {
  pageReferrerStateActions,
  usePageReferrerState,
} from '@/states/pageReferrerState';

// Delay before sending GA4 events to ensure user authentication and metadata are resolved
const GA_EVENT_DELAY_MS = 500;

export const GoogleAnalyticsProvider = ({ children }: WithChildren) => {
  const { asPath, pathname } = useRouter();
  const { sendEvent } = useGoogleAnalytics();
  const { pageReferrer } = usePageReferrerState();

  useEffect(() => {
    window._uxa = window._uxa || [];
    window._uxa.push(['setPIISelectors', { PIISelectors: ['.pii'] }]);
  }, []);

  useEffect(() => {
    if (pathname === '/404' || isWholeSalerWidget(pathname)) return;

    // Delay GA4 event to allow user authentication and metadata to resolve
    const timeoutId = setTimeout(() => {
      sendEvent({
        event: EVENT_NAME.PAGE_VIEW,
        page_referrer: pageReferrer || document.referrer,
      });
    }, GA_EVENT_DELAY_MS);

    // Update referrer state immediately (no delay needed)
    pageReferrerStateActions.setPageReferrer(window.location.href);
    pageReferrerStateActions.setPrevPageReferrer(pageReferrer);

    return () => clearTimeout(timeoutId);
  }, [pathname, asPath, sendEvent, pageReferrer]);

  return <>{children}</>;
};
